import React, { useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Sparkles, Infinity as InfIcon, MapPin, Copy, Gift, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  "Unlimited trips",
  "Unlimited AI itineraries & packing lists",
  "Priority AI generation",
  "Document Vault (passports, tickets, PDFs)",
  "Collaborator invites (view or edit)",
  "PDF booklet export",
];

const RZP_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Upgrade() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phone, setPhone] = useState(() => localStorage.getItem("wl_phone") || "");
  const [pendingPkg, setPendingPkg] = useState(null);
  const isPro = user?.plan === "pro";

  const validPhone = (v) => /^[6-9]\d{9}$/.test(v.replace(/\s/g, ""));

  const startCheckout = (pkg) => {
    if (!validPhone(phone)) {
      setPendingPkg(pkg);
      setPhoneOpen(true);
      return;
    }
    checkout(pkg);
  };

  const submitPhone = () => {
    const clean = phone.replace(/\s/g, "");
    if (!validPhone(clean)) {
      toast.error("Enter a valid 10-digit Indian mobile (starts with 6/7/8/9)");
      return;
    }
    setPhone(clean);
    localStorage.setItem("wl_phone", clean);
    setPhoneOpen(false);
    if (pendingPkg) { const p = pendingPkg; setPendingPkg(null); checkout(p); }
  };

  const checkout = useCallback(async (pkg) => {
    setBusy(pkg);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay checkout");
      const { data: order } = await api.post("/payments/razorpay/order", { package_id: pkg });

      const savedPhone = (localStorage.getItem("wl_phone") || phone || "").replace(/\s/g, "");
      const contactE164 = savedPhone ? (savedPhone.startsWith("+") ? savedPhone : "+91" + savedPhone) : "";
      const rzp = new window.Razorpay({
        key: order.key_id || RZP_KEY,
        amount: order.amount,
        currency: order.currency,
        name: "WanderLoom",
        description: pkg === "pro_yearly" ? "Pro Yearly — ₹5,000" : "Pro Monthly — ₹700",
        order_id: order.order_id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: contactE164,
        },
        theme: { color: "#C85A32" },
        modal: {
          ondismiss: () => {
            setBusy(null);
            toast.info("Checkout cancelled — you weren't charged");
          },
        },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await refresh();
            toast.success("Welcome to Pro — unlimited trips & AI unlocked");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          } finally {
            setBusy(null);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        setBusy(null);
        toast.error(resp?.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (e) {
      setBusy(null);
      toast.error(e?.response?.data?.detail || e.message || "Checkout failed");
    }
  }, [user, refresh, phone]);

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar/>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-center mb-12">
          <div className="font-mono-code text-xs uppercase tracking-[0.3em] text-amber-700 mb-3">
            <Sparkles size={12} className="inline mr-1"/> WanderLoom Pro
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900">
            Travel the world, <em className="text-[#C85A32]">unbound</em>.
          </h1>
          <p className="text-stone-600 mt-4 max-w-xl mx-auto">
            Unlimited trips. Unlimited AI. Everything you need to plan every journey, forever.
          </p>
        </div>

        {isPro && (() => {
          const exp = user?.pro_expires_at;
          const daysLeft = exp ? Math.max(0, Math.ceil((new Date(exp) - new Date()) / (1000*60*60*24))) : null;
          return (
            <div className="mb-10 p-6 rounded-2xl border border-emerald-300 bg-emerald-50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="font-mono-code text-[10px] uppercase tracking-widest text-emerald-800">Current plan</div>
                  <div className="font-serif-display text-2xl font-semibold text-emerald-900 mt-1">Pro · {user?.pro_package === "pro_yearly" ? "Yearly" : "Monthly"}</div>
                  <div className="text-sm text-emerald-800 mt-1" data-testid="pro-expiry-text">
                    {exp ? <>Ends on <span className="font-mono-code">{exp.slice(0,10)}</span> · {daysLeft} days left</> : "Active"}
                  </div>
                  <div className="text-xs text-emerald-700 mt-2 max-w-md">
                    Your Pro pass doesn't auto-renew — you won't be charged again. Renew anytime below to extend, or let it lapse to return to Free.
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Monthly */}
          <div className="rounded-2xl border border-stone-200 bg-white p-8 relative">
            <div className="font-mono-code text-[10px] uppercase tracking-[0.25em] text-stone-500">Monthly</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-serif-display text-5xl font-bold">₹700</div>
              <div className="text-stone-500">/ month</div>
            </div>
            <div className="text-xs font-mono-code text-stone-500 mt-1">Billed monthly. Cancel anytime.</div>
            <ul className="mt-6 space-y-2">
              {FEATURES.map(f => (
                <li key={f} className="flex items-center gap-2 text-sm text-stone-700">
                  <Check size={16} className="text-[#C85A32]"/> {f}
                </li>
              ))}
            </ul>
            <Button
              className="w-full mt-8 h-12 bg-stone-900 hover:bg-stone-800 text-white rounded-xl"
              disabled={busy === "pro_monthly"}
              onClick={() => startCheckout("pro_monthly")}
              data-testid="upgrade-monthly-button"
            >
              {busy === "pro_monthly" ? "Opening Razorpay…" : isPro ? "Renew — ₹700/mo" : "Upgrade — ₹700/mo"}
            </Button>
          </div>

          {/* Yearly */}
          <div className="rounded-2xl border-2 border-[#C85A32] bg-gradient-to-br from-amber-50 to-orange-50 p-8 relative overflow-hidden">
            <div className="absolute top-4 right-4 px-2 py-1 rounded-full bg-[#C85A32] text-white text-[10px] font-mono-code uppercase tracking-widest">Save 40%</div>
            <div className="font-mono-code text-[10px] uppercase tracking-[0.25em] text-amber-800">Yearly</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-serif-display text-5xl font-bold">₹5,000</div>
              <div className="text-stone-600">/ year</div>
            </div>
            <div className="text-xs font-mono-code text-amber-800 mt-1">₹417/mo · Billed annually. Best value.</div>
            <ul className="mt-6 space-y-2">
              {FEATURES.map(f => (
                <li key={f} className="flex items-center gap-2 text-sm text-stone-800">
                  <Check size={16} className="text-[#C85A32]"/> {f}
                </li>
              ))}
              <li className="flex items-center gap-2 text-sm text-stone-800 font-semibold">
                <InfIcon size={16} className="text-[#C85A32]"/> Two months free
              </li>
            </ul>
            <Button
              className="w-full mt-8 h-12 bg-[#C85A32] hover:bg-[#B04B26] text-white rounded-xl"
              disabled={busy === "pro_yearly"}
              onClick={() => startCheckout("pro_yearly")}
              data-testid="upgrade-yearly-button"
            >
              {busy === "pro_yearly" ? "Opening Razorpay…" : isPro ? "Renew — ₹5,000/yr" : "Upgrade — ₹5,000/yr"}
            </Button>
          </div>
        </div>

        <div className="mt-10 text-center text-xs font-mono-code text-stone-500">
          <ShieldCheck size={12} className="inline mr-1"/> Payments processed securely via Razorpay · UPI, cards, netbanking &amp; wallets. Test card: 4111 1111 1111 1111.
        </div>

        {/* Referral card */}
        {user?.referral_code && (
          <div className="mt-12 rounded-2xl border border-amber-200 bg-white p-6" data-testid="referral-card">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
                <Gift size={22} className="text-[#C85A32]"/>
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="font-mono-code text-[10px] uppercase tracking-[0.25em] text-amber-700">Traveler referrals</div>
                <h3 className="font-serif-display text-2xl font-semibold text-stone-900 mt-1">Gift a month, get a month.</h3>
                <p className="text-sm text-stone-600 mt-1 max-w-lg">
                  Share your code. Every friend who signs up gets 30 days of Pro on us — and your plan gets extended by 30 days too.
                </p>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <div className="px-3 py-2 rounded-lg bg-stone-950 text-white font-mono-code text-sm" data-testid="referral-code-value">{user.referral_code}</div>
                  <Button size="sm" variant="outline" onClick={() => {
                    navigator.clipboard.writeText(user.referral_code);
                    toast.success("Code copied");
                  }} data-testid="referral-copy-code"><Copy size={14} className="mr-1"/> Copy code</Button>
                  <Button size="sm" className="bg-[#C85A32] hover:bg-[#B04B26]" onClick={() => {
                    const link = `${window.location.origin}/?ref=${encodeURIComponent(user.referral_code)}`;
                    navigator.clipboard.writeText(link);
                    toast.success("Invite link copied");
                  }} data-testid="referral-copy-link"><Copy size={14} className="mr-1"/> Copy invite link</Button>
                </div>
                {user.referral_credits > 0 && (
                  <div className="mt-3 font-mono-code text-xs text-emerald-700">
                    {user.referral_credits} friend{user.referral_credits === 1 ? "" : "s"} joined · {user.referral_credits * 30} bonus days earned
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <Dialog open={phoneOpen} onOpenChange={setPhoneOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-md" data-testid="phone-collect-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">One quick detail</DialogTitle>
            <DialogDescription>
              Razorpay needs a 10-digit Indian mobile number to send your payment receipt. This is stored locally in your browser.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="wl-phone">Mobile number</Label>
            <div className="flex items-center gap-2">
              <div className="px-3 h-10 rounded-md border border-stone-300 bg-white flex items-center font-mono-code text-sm text-stone-700">🇮🇳 +91</div>
              <Input
                id="wl-phone"
                inputMode="numeric"
                maxLength={10}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder="9876543210"
                className="bg-white"
                data-testid="phone-input"
                autoFocus
              />
            </div>
            <div className="text-xs text-stone-500">Must start with 6, 7, 8 or 9 · exactly 10 digits</div>
            <Button
              onClick={submitPhone}
              className="w-full bg-[#C85A32] hover:bg-[#B04B26]"
              data-testid="phone-submit-button"
            >
              Continue to payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
